import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ChartBarIcon,
  WifiIcon
} from '@heroicons/react/24/outline';
import type { AlertStats as AlertStatsType } from '../api/alertService';

interface AlertStatsProps {
  stats: AlertStatsType;
  loading?: boolean;
}

export default function AlertStats({ stats, loading = false }: AlertStatsProps) {
  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white overflow-hidden shadow rounded-lg animate-pulse">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="h-8 w-8 bg-gray-300 rounded"></div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <div className="h-4 bg-gray-300 rounded w-16 mb-2"></div>
                  <div className="h-6 bg-gray-300 rounded w-8"></div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  const statCards = [
    {
      name: 'Active Alerts',
      value: stats.active,
      icon: ExclamationTriangleIcon,
      color: stats.active > 0 ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-50',
      change: null,
      href: '#',
    },
    {
      name: 'Acknowledged',
      value: stats.acknowledged,
      icon: ClockIcon,
      color: 'text-yellow-600 bg-yellow-50',
      change: null,
      href: '#',
    },
    {
      name: 'Resolved',
      value: stats.resolved,
      icon: CheckCircleIcon,
      color: 'text-green-600 bg-green-50',
      change: null,
      href: '#',
    },
    {
      name: 'Total Alerts',
      value: stats.total,
      icon: ChartBarIcon,
      color: 'text-blue-600 bg-blue-50',
      change: null,
      href: '#',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Main Stats */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((item) => (
          <div key={item.name} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`p-2 rounded-lg ${item.color}`}>
                    <item.icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                    <dd className="text-lg font-medium text-gray-900">{item.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Detailed Breakdown */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
        {/* By Type */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Alerts by Type</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <ChartBarIcon className="h-4 w-4 text-blue-500" />
                  <span className="text-sm text-gray-600">Data Gap</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.byType.dataGap}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <WifiIcon className="h-4 w-4 text-red-500" />
                  <span className="text-sm text-gray-600">Gateway Offline</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.byType.gatewayOffline}</span>
              </div>
            </div>
          </div>
        </div>

        {/* By Severity */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Alerts by Severity</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 rounded-full bg-red-500"></div>
                  <span className="text-sm text-gray-600">Critical</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.bySeverity.critical}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 rounded-full bg-orange-500"></div>
                  <span className="text-sm text-gray-600">High</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.bySeverity.high}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm text-gray-600">Medium</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.bySeverity.medium}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <div className="h-3 w-3 rounded-full bg-blue-500"></div>
                  <span className="text-sm text-gray-600">Low</span>
                </div>
                <span className="text-sm font-medium text-gray-900">{stats.bySeverity.low}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Health Indicator */}
      <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">System Health</h3>
              <p className="mt-1 text-sm text-gray-500">
                Overall status based on active alerts
              </p>
            </div>
            <div className="flex items-center space-x-2">
              {stats.active === 0 ? (
                <>
                  <CheckCircleIcon className="h-6 w-6 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Healthy</span>
                </>
              ) : stats.bySeverity.critical > 0 ? (
                <>
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500" />
                  <span className="text-sm font-medium text-red-600">Critical Issues</span>
                </>
              ) : stats.bySeverity.high > 0 ? (
                <>
                  <ExclamationTriangleIcon className="h-6 w-6 text-orange-500" />
                  <span className="text-sm font-medium text-orange-600">High Priority Issues</span>
                </>
              ) : (
                <>
                  <ClockIcon className="h-6 w-6 text-yellow-500" />
                  <span className="text-sm font-medium text-yellow-600">Minor Issues</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}